import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import type { MultiValue } from 'react-select';

import FormRow from '../../../app/components/FormRow';
import { CreateUpdateProjectWithTeamDto } from '../../../common/pokCore/autogenerated/pokApiClient/models';
import DictionarySelector from '../Selects/DictionarySelector';
import DynamicTextarea from '../../../app/components/DynamicTextarea';
import EmployeeSelector from '../Selects/EmployeeSelector';
import { Option } from '../Selects/Selector';
import { useTranslation } from '../../../common/hooks/useTranslation';
import ProfileSelector from '../Selects/ProfileSelector';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import DictionaryMultiSelector from '../Selects/DictionaryMultiSelector';
import { TooltipKey } from '../../../common/pokCore/tooltip.enum';

import { ProjectDatesForm } from './ProjectDatesForm';

interface ProjectMainFieldsForm3Props {
  project: CreateUpdateProjectWithTeamDto;
  readOnly?: boolean;
  isCreatedFromOrder?: boolean;
  orderPhaseId?: string;
  budgetId?: string;
  blockedDates?: boolean;
  propertyChange?: (obj: Partial<CreateUpdateProjectWithTeamDto>) => void;
  datesChanged: (dates: { startDate?: Date; endDate?: Date }) => void;
}

export const ProjectMainFieldsForm3: React.FC<
  ProjectMainFieldsForm3Props
> = props => {
  const { t, tk } = useTranslation('projectLng');
  const pok = usePokCore();
  const notifications = useNotifications();
  const [pathPhaseEnabled, setPathPhaseEnabled] = useState(false);

  useEffect(() => {
    pok.companies
      .getById(pok.getCompanyId())
      .then(company => setPathPhaseEnabled(company.pathPhase))
      .catch(errorResponse => notifications.caughtError(errorResponse));
  }, [pok, notifications]);

  const handleEmployeeSelected = async (
    option: Option | MultiValue<Option> | null | undefined,
  ) => {
    if (option && (option as Option)?.value) {
      if (props.propertyChange) {
        props.propertyChange({ responsibleOwnerId: (option as Option)?.value });
      }
    }
  };

  return (
    <>
      <FormRow controlId="country" label={t(tk.projects.country)}>
        <DictionaryMultiSelector
          dictionary="Kraj/rynek"
          value={props.project.projectCountriesIds}
          clearable={true}
          readOnly={props.readOnly}
          onChange={options =>
            props.propertyChange &&
            props.propertyChange({
              projectCountriesIds:
                (options as Option[])?.map(({ value }) => value) || [],
            })
          }
        />
      </FormRow>
      {pathPhaseEnabled &&
        !(props.isCreatedFromOrder && !props.orderPhaseId) && (
          <FormRow
            controlId="pathPhase"
            label={t(tk.projects.pathPhases)}
            tooltipKey={
              props.orderPhaseId
                ? TooltipKey.projectFromOrderPathPhasesTooltip
                : TooltipKey.projectPathPhasesTooltip
            }
          >
            <DictionaryMultiSelector
              dictionary="Faza"
              value={props.project.projectPathPhasesIds}
              clearable={!props.orderPhaseId}
              readOnly={props.readOnly || !!props.orderPhaseId}
              onChange={options =>
                props.propertyChange &&
                props.propertyChange({
                  projectPathPhasesIds:
                    (options as Option[])?.map(({ value }) => value) || [],
                })
              }
            />
          </FormRow>
        )}
      {!props.readOnly && !props.project.id && (
        <FormRow
          controlId="profileId"
          label={`${t(tk.common.profile)} ${t(tk.prLang.estimate2)}`}
        >
          <ProfileSelector
            provider={pok.profiles.getAllByCompanyIdsTeamIdsAuth}
            companyIds={
              props.project.companyId ? [props.project.companyId] : []
            }
            teamIds={props.project.teamIds}
            budgetId={props.budgetId}
            readOnly={props.project.teamIds.length === 0}
            onChange={option =>
              props.propertyChange &&
              props.propertyChange({
                profileId: option ? (option as Option).value : '',
              })
            }
          />
        </FormRow>
      )}
      {!props.readOnly && (
        <ProjectDatesForm
          project={props.project}
          propertyChange={props.propertyChange}
          datesChanged={props.datesChanged}
          blockedDates={props.blockedDates}
        />
      )}
      <FormRow
        controlId="purchaserDocNumber"
        label={t(tk.projects.purchaserDocNumber)}
      >
        <Form.Control
          type="text"
          value={props.project.purchaserDocNumber || ''}
          onChange={e =>
            props.propertyChange &&
            props.propertyChange({ purchaserDocNumber: e.target.value })
          }
          disabled={props.readOnly}
        />
      </FormRow>
      <FormRow controlId="notes" label={t(tk.projects.note)}>
        <DynamicTextarea
          value={props.project.notes || ''}
          onChange={e =>
            props.propertyChange &&
            props.propertyChange({ notes: e.target.value })
          }
          disabled={props.readOnly}
        />
      </FormRow>
      {props.project.id && (
        <FormRow
          controlId="responsibleOwner"
          label={t(tk.common.responsiblePerson)}
        >
          <EmployeeSelector
            value={props.project.responsibleOwnerId || ''}
            onChange={handleEmployeeSelected}
            readOnly={props.readOnly}
          />
        </FormRow>
      )}
      <FormRow controlId="labelId" label={t(tk.prLang.projectLabel)}>
        <DictionarySelector
          dictionary="Etykieta kampanii"
          value={props.project.labelDictionaryId}
          clearable={true}
          readOnly={props.readOnly}
          onChange={option =>
            props.propertyChange &&
            props.propertyChange({
              labelDictionaryId: option?.value,
            })
          }
        />
      </FormRow>
    </>
  );
};
